import { z } from "zod";

/**
 * Tenant-uploaded storefront images (logo, favicon, banner, badge). SVG is refused:
 * small line-art icons stay as validated SVG source on the config row (`svgIcon.ts`);
 * rasters go to the stage's tenant-assets bucket.
 */
export const TENANT_ASSET_KINDS = ["logo", "favicon", "banner", "badge"] as const;
export type TenantAssetKind = (typeof TENANT_ASSET_KINDS)[number];
export const tenantAssetKindSchema = z.enum(TENANT_ASSET_KINDS);

export const TENANT_ASSET_CONTENT_TYPES = [
  "image/png",
  "image/jpeg",
  "image/webp",
  "image/avif",
] as const;
export type TenantAssetContentType = (typeof TENANT_ASSET_CONTENT_TYPES)[number];
export const tenantAssetContentTypeSchema = z.enum(TENANT_ASSET_CONTENT_TYPES);

/**
 * Ceiling on one upload. The browser PUTs the file straight to the bucket, so this is a product
 * limit, not the Lambda payload cap; it is signed into the upload URL and checked again after.
 */
export const MAX_TENANT_ASSET_BYTES = 5 * 1024 * 1024;

/** What the dashboard sends to start an upload: the file's type and size, signed into the URL. */
export const tenantAssetUploadStartSchema = z.object({
  contentType: tenantAssetContentTypeSchema,
  byteLength: z.int().positive().max(MAX_TENANT_ASSET_BYTES),
});

export const TENANT_ASSET_EXTENSIONS = {
  "image/png": "png",
  "image/jpeg": "jpg",
  "image/webp": "webp",
  "image/avif": "avif",
} as const satisfies Record<TenantAssetContentType, string>;

/** `accept` on the dashboard file input — SVG is omitted on purpose. */
export const TENANT_ASSET_FILE_ACCEPT =
  ".png,.jpg,.jpeg,.webp,.avif,image/png,image/jpeg,image/webp,image/avif";

export function isTenantAssetKind(value: string): value is TenantAssetKind {
  return (TENANT_ASSET_KINDS as readonly string[]).includes(value);
}

export function isTenantAssetContentType(value: string): value is TenantAssetContentType {
  return (TENANT_ASSET_CONTENT_TYPES as readonly string[]).includes(value);
}
